import { MindspaceRole } from "~/graphql/codegen/graphql";

// Champion / coach gates over Mindspace.currentUserPermission (and the member
// rows of the Organization table). `role` is the stored membership column: after
// the C2 legacy-role remap a champion or coach reads `member` there, with the
// champion / coach carried by `isChampion` / `isCoach` — the server's dual-read
// answer (legacy role OR exactly the remap's shape, not flag-gated). So "is this
// person a champion / coach?" reads those, never `role`; "is this an admin?"
// stays on `role`.

type RoleAxes = {
  role: MindspaceRole;
  isChampion: boolean;
  isCoach: boolean;
};

type ViewerPermission = RoleAxes | null | undefined;

/** Admin, champion or coach (Organization navigation, cohort-organization pages). */
export function isAdminChampionOrCoach(permission: ViewerPermission): boolean {
  if (!permission) return false;
  return (
    permission.role === MindspaceRole.Admin ||
    permission.isChampion ||
    permission.isCoach
  );
}

/** Admin or coach. */
export function isAdminOrCoach(permission: ViewerPermission): boolean {
  if (!permission) return false;
  return permission.role === MindspaceRole.Admin || permission.isCoach;
}

/**
 * Admin or a member who is neither champion nor coach — the legacy role list
 * [admin, member], which never included a champion / coach row, so a remapped
 * one (stored role `member`) keeps the answer its legacy row had.
 */
export function isAdminOrPlainMember(permission: ViewerPermission): boolean {
  if (!permission) return false;
  if (permission.role === MindspaceRole.Admin) return true;
  return (
    permission.role === MindspaceRole.Member &&
    !permission.isChampion &&
    !permission.isCoach
  );
}

/**
 * The role a member row DISPLAYS: a remapped champion / coach reads as its
 * legacy row did, every other row its stored role (coach wins on a member row
 * carrying both, as on the server's effective role). Display only — writers
 * send the role the person picks.
 */
export function effectiveMemberRole(member: RoleAxes): MindspaceRole {
  if (member.role !== MindspaceRole.Member) return member.role;
  if (member.isCoach) return MindspaceRole.Coach;
  if (member.isChampion) return MindspaceRole.Champion;
  return member.role;
}

/**
 * May check another person in to an event (the QR scanner): an admin only, as
 * the server's mindspaceEventAttend requires (FOX-6404).
 */
export function mayCheckInOthers(permission: ViewerPermission): boolean {
  return permission?.role === MindspaceRole.Admin;
}
